<template>
  <div class="payment-apis">
    <div v-show="!failedPayment">
      <div class="form-field">
        <input
          id="card-holder-name"
          name="card-holder-name"
          aria-label="Card holder name"
          v-model="cardHolderName"
          type="text"
          placeholder="Card holder name"
          ref="cardHolderName"
          autocomplete="nope"
          @input="readyToSave()"
          required
        />
      </div>

      <div class="form-field">
        <div id="card-number" :class="{ 'input-error': cardNumberError }"></div>
      </div>

      <div class="cvv-form-field">
        <div id="card-expiry" :class="{ 'input-error': cardExpiryError }"></div>
        <div id="card-cvv" :class="{ 'input-error': cardCVVError }"></div>
      </div>
    </div>

    <div class="flex flex-col items-center" v-if="!failedPayment && showButton">
      <button
        type="button"
        class="pay-now"
        :class="{ disabled: !activatePayButton() }"
        :disabled="!activatePayButton()"
        href="#form-top"
        data-animation-duration="300"
        data-animation-easing="swing"
        id="pay-button"
        @click="payNow"
        data-smooth-scroll
        data-threshold="75"
      >
        {{ submitButtonMessage }}
      </button>
    </div>

    <!-- Error Messages -->
    <Alert v-if="errorMessage" :message="errorMessage"></Alert>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Alert from '@/components/Alert.vue';
import { ALERT_TYPE } from '@/helpers/constants';

export default {
  name: 'BamboraCreditCardForm',
  components: {
    Alert,
  },
  data() {
    return {
      isCardNumberComplete: false,
      isCVVComplete: false,
      isExpiryComplete: false,
      isPayButtonDisabled: false,
      cardHolderName: '',
      cardHolderNameError: undefined,
      cardNumberError: undefined,
      cardCVVError: undefined,
      cardExpiryError: undefined,
      customCheckout: window.customcheckout(),
      serverError: undefined,
      bamboraInputs: {
        cardNumber: undefined,
        expiryTime: undefined,
        cvvNumber: undefined,
      },
      failedPayment: false,
      message: null,
    };
  },
  props: {
    submitButtonMessage: {
      type: String,
      default: 'Pay Now',
    },
    showButton: {
      type: Boolean,
      default: true,
    },
  },
  mounted() {
    console.log('mounted');
    this.initForm();
    this.initCustomeCheckoutEvents();
  },
  // beforeDestroy() {
  //   console.log(window);

  //   this.bamboraInputs.cardNumber.unmount();
  //   this.bamboraInputs.cvvNumber.unmount();
  //   this.bamboraInputs.expiryTime.unmount();
  //   console.log('unmounted');
  // },
  computed: {
    ...mapState({
      donation: (state) => state.donation.donation,
    }),
    errorMessage: {
      get() {
        return this.message;
      },
      set(value) {
        if (value === null) {
          this.message = null;
        } else {
          this.message = {
            content: value,
            type: ALERT_TYPE.ERROR,
          };
        }
      },
    },
  },
  methods: {
    initForm: function () {
      // Set the font size for the inputs inside the injected iframes
      const style = {
        base: {
          fontSize: '15px',
        },
        error: {
          color: 'red',
        },
      };
      const options = {
        style,
      };

      this.cardHolderName = '';

      // Create and mount the inputs
      options.placeholder = 'Card number';
      this.bamboraInputs.cardNumber = this.customCheckout.create('card-number', options);
      this.bamboraInputs.cardNumber.mount('#card-number');

      options.placeholder = 'CVV / CVC';
      this.bamboraInputs.cvvNumber = this.customCheckout.create('cvv', options);
      this.bamboraInputs.cvvNumber.mount('#card-cvv');

      options.placeholder = 'MM / YY';
      this.bamboraInputs.expiryTime = this.customCheckout.create('expiry', options);
      this.bamboraInputs.expiryTime.mount('#card-expiry');
    },
    initCustomeCheckoutEvents: function () {
      // Display card brand logo in the card number input
      this.customCheckout.on('brand', (event) => {
        let cardLogo = 'none';
        if (event.brand && event.brand !== 'unknown') {
          const filePath = `https://cdn.na.bambora.com/downloads/images/cards/${event.brand}.svg`;
          cardLogo = 'url(' + filePath + ')';
        }
        document.getElementById('card-number').style.backgroundImage = cardLogo;
      });

      // Empty
      this.customCheckout.on('empty', (event) => {
        if (event.empty) {
          if (event.field === 'card-number') {
            this.errorMessage = null;
            this.isCardNumberComplete = false;
          } else if (event.field === 'cvv') {
            this.errorMessage = null;
            this.isCVVComplete = false;
          } else if (event.field === 'expiry') {
            this.errorMessage = null;
            this.isExpiryComplete = false;
          }
        }
      });

      // Error msg
      this.customCheckout.on('error', (event) => {
        console.log(event);
        this.errorMessage = event.message;
      });

      // Completed
      this.customCheckout.on('complete', (event) => {
        if (event.field === 'card-number') {
          this.serverError = '';
          this.errorMessage = null;
          this.cardNumberError = undefined;
          this.isCardNumberComplete = event.complete;
          console.log('Card number is complete: ', event);
        } else if (event.field === 'cvv') {
          this.serverError = '';
          this.errorMessage = null;
          this.cardCVVError = undefined;
          this.isCVVComplete = event.complete;
          console.log('CVV is complete: ', event);
        } else if (event.field === 'expiry') {
          this.serverError = '';
          this.errorMessage = null;
          this.cardExpiryError = undefined;
          this.isExpiryComplete = event.complete;
          console.log('Expiry is complete: ', event);
        }
        this.readyToSave();
      });
    },
    resetForm: function () {
      this.bamboraInputs.expiryTime.clear();
      this.bamboraInputs.cardNumber.clear();
      this.bamboraInputs.cvvNumber.clear();
      this.cardHolderName = '';

      // No need to reset the "complete" flags. In case of copy paste of a good card, the "complete" trigger won't fire.
      // If the user enter a wrong card number, the "error" trigger will fire anyway.
      // Trying to pay with empty field will just fail the payment.

      document.getElementById('card-number').style.backgroundImage = '';
    },
    resetFormErrors: function () {
      this.cardNumberError = undefined;
      this.cardCVVError = undefined;
      this.cardExpiryError = undefined;
      this.serverError = undefined;
      this.cardHolderName = '';
    },
    togglePayButtonDisabled: function (value) {
      //disable pay-button
      this.isPayButtonDisabled = value;
    },
    cardHolderNameIsValid: function () {
      return this.cardHolderName && this.cardHolderName.length > 3;
    },
    readyToSave() {
      if (!this.showButton) {
        console.log(this.activatePayButton());
        if (this.activatePayButton()) {
          this.$emit('readyToSave');
          this.customCheckout.createOneTimeToken(process.env.VUE_APP_BAMBORA_MERCHANT_ID, this.processToken);
        } else {
          this.$emit('notReady');
        }
      }
    },
    activatePayButton: function () {
      // Activate Pay Button only if all the field are complete and there is no errors
      return (
        this.isCardNumberComplete &&
        this.isExpiryComplete &&
        this.isCVVComplete &&
        !this.cardNumberError &&
        !this.cardCVVError &&
        !this.cardExpiryError &&
        !this.serverError &&
        this.cardHolderNameIsValid() &&
        !this.isPayButtonDisabled
      );
    },
    payNow: function () {
      this.$emit('startProcessingPayment');
      this.togglePayButtonDisabled(true);
      console.log('Pay::customCheckout.createOneTimeToken()');
      this.customCheckout.createOneTimeToken(process.env.VUE_APP_BAMBORA_MERCHANT_ID, this.processToken);
      this.$emit('finishProcessingPayment');
    },
    processToken: function (result) {
      console.log('token result : ', result);
      if (result.error) {
        this.processTokenError(result.error);
      } else {
        console.log(`TOKEN RESULT`, result);
        this.processTokenSuccess(result.token);
      }
    },
    processTokenError: function (error) {
      console.log('processTokenError: ', error);
      if (error.message) {
        this.serverError = error.message;
      } else {
        this.serverError = 'Unable to process transaction. Please try again later.';
      }
      this.togglePayButtonDisabled(false);
      this.failedPayment = true;
      this.$emit('error');
    },
    processTokenSuccess: function (token) {
      console.log('processTokenSuccess: ' + token);
      this.serverError = undefined;

      this.$emit('success', {
        token,
        cardHolderName: this.cardHolderName,
      });
    },
    reset: function (error) {
      this.resetForm();
      if (error) {
        this.serverError = error;
        this.failedPayment = true;
      } else {
        this.resetFormErrors();
        this.failedPayment = false;
      }
      this.togglePayButtonDisabled(false);
    },
  },
};
</script>

<!-- This Style need to be not scoped ! -->
<style>
.wwf-form #Step3 .payment-apis iframe {
  height: 15px;
}
</style>

<style scoped>
.cvv-form-field {
  display: flex;
  flex-direction: row;
}

#card-holder-name,
#card-number,
#card-cvv,
#card-expiry {
  background-color: #fff;
  display: block;
  border: 1px solid #c8c8c8;
  padding: 14px 60px 13px 20px;
  margin: auto;
  transition: all 100ms ease-out;
  background-color: rgba(0, 0, 0, 0.05);
}

#card-holder-name:focus {
  box-shadow: inset 0 0 0 2px #3f9ffd;
}

#card-number.bambora-checkoutfield-focus,
#card-cvv.bambora-checkoutfield-focus,
#card-expiry.bambora-checkoutfield-focus {
  box-shadow: inset 0 0 0 2px #3f9ffd;
}

#card-holder-name {
  margin-bottom: 30px;
  height: 53px;
}

#card-holder-name,
#card-number {
  margin-top: 5px;
  border-radius: 2px 2px 0 0;
  background-image: none;
  background-origin: content-box;
  background-position: calc(100% + 40px) center;
  background-repeat: no-repeat;
  background-size: contain;
}

#card-holder-name::-webkit-input-placeholder {
  font: 500 15px Arial !important;
  color: #555 !important;
}

#card-cvv {
  border-bottom-right-radius: 2px;
  border-top: none;
  border-left: none;
}

#card-expiry {
  border-bottom-left-radius: 2px;
  border-top: none;
}

#card-cvv,
#card-expiry {
  width: 100%;
  display: inline-block;
  margin-bottom: 30px;
}

.payment-apis .pay-now {
  text-transform: initial;
  font-size: 22px;
  line-height: 48px;
  height: auto;
  background-color: #3f9ffd !important;
}

.payment-apis .pay-now.disabled {
  cursor: default;
  background-color: #dedede !important;
}

.errors {
  margin-bottom: 20px;
}

.errors label {
  font-size: 15px;
  color: #ff003b;
  display: block;
}

.payment-apis .input-error {
  border: 2px solid #ff003b !important;
}
</style>
